import React from 'react';
import { MapPinIcon, ChevronDownIcon, PlusIcon, MinusIcon } from 'lucide-react';
import { sectorGrid, selectedSector, sectorDetail, type Status } from '../../../data/requestsIntelligence';
type Props = {
  isFR: boolean;
};
const FILL: Record<Status, string> = {
  red: '#F07A7A',
  amber: '#F6C77A',
  green: '#9BD9A8'
};
const FILL_SOFT: Record<Status, string> = {
  red: '#F7A9A6',
  amber: '#F9DDA9',
  green: '#C3E9CA'
};

// Jittered grid → irregular municipal sectors.
const COLS = 6;
const ROWS = 4;
const X0 = 150;
const X1 = 540;
const Y0 = 14;
const Y1 = 300;
const JITTER: [number, number][][] = [[[0, 0], [4, -6], [-3, 5], [6, -2], [-4, 4], [3, 0], [0, 0]], [[-10, 6], [7, -5], [-6, 8], [9, 4], [-7, -6], [5, 7], [0, -4]], [[-18, -4], [-5, 9], [8, -7], [-6, 6], [10, -5], [-4, 8], [0, 5]], [[-26, 5], [9, -6], [-7, 5], [5, -8], [-9, 6], [7, -4], [0, -3]], [[-34, 0], [-4, 0], [6, 0], [-5, 0], [8, 0], [-3, 0], [0, 0]]];
function point(r: number, c: number): [number, number] {
  const x = X0 + (X1 - X0) / COLS * c;
  const y = Y0 + (Y1 - Y0) / ROWS * r;
  const [jx, jy] = JITTER[r][c];
  return [x + jx, y + jy];
}
function cellPath(r: number, c: number) {
  const pts = [point(r, c), point(r, c + 1), point(r + 1, c + 1), point(r + 1, c)];
  return `M${pts.map(p => p.join(' ')).join(' L')} Z`;
}
function cellCenter(r: number, c: number): [number, number] {
  const pts = [point(r, c), point(r, c + 1), point(r + 1, c + 1), point(r + 1, c)];
  return [pts.reduce((s, p) => s + p[0], 0) / 4, pts.reduce((s, p) => s + p[1], 0) / 4];
}
export function SectorHeatmap({
  isFR
}: Props) {
  const t = (fr: string, en: string) => isFR ? fr : en;
  const [cx, cy] = cellCenter(selectedSector.row, selectedSector.col);
  return <div className="rounded-xl border border-[#ECECF3] bg-white p-4 h-full flex flex-col" data-id="element-5881" data-mp="2ewq4948omjip">
      <div className="flex items-start justify-between gap-3" data-id="element-5882" data-mp="18i78g69fwzpb">
        <div className="flex items-start gap-2.5" data-id="element-5883" data-mp="13im7xremgjah">
          <span className="w-8 h-8 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-5884" data-mp="13grpqkuqj5a3">
            <MapPinIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} data-id="element-5885" data-mp="14xozou4lz0s4" />
          </span>
          <div data-id="element-5886" data-mp="19w8mewr10glj">
            <p className="font-sans text-[13.5px] font-semibold text-[#121212]" data-id="element-5887" data-mp="vytpl6yg16jj">{t('Carte des secteurs', 'Sector map')}</p>
            <p className="font-sans text-[11px] text-[#8A90A0]" data-id="element-5888" data-mp="zznhebf0b5hp">{t('Délais de traitement par secteur', 'Processing times by sector')}</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-md border border-[#E6E7EE] px-2.5 py-1.5 font-sans text-[10.5px] text-[#4A5160] whitespace-nowrap" data-id="element-5889" data-mp="m77b91qt6rck">
          {t('Tous les services', 'All services')}
          <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-5890" data-mp="h9nsboj61qea" />
        </span>
      </div>

      <div className="relative mt-3 flex-1 min-h-[240px] rounded-lg overflow-hidden bg-[#F2F3F0]" data-id="element-5891" data-mp="3aefmyu65ef2">
        <svg viewBox="0 0 560 310" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full" role="img" aria-label={t('Carte thermique des secteurs : le secteur A et ses voisins sont sous la cible, le centre est à surveiller, l’est est en bonne voie.', 'Sector heatmap: Sector A and its neighbours are below target, the centre needs attention, the east is on track.')} data-id="element-5892" data-mp="88zf2w1lju2g">
          {/* parks on west bank */}
          <path d="M0 40 C 40 30, 70 60, 60 110 C 50 150, 10 160, 0 150 Z" fill="#D6ECD6" data-id="element-5893" data-mp="7l9uvb8ywjfk" />
          <path d="M0 210 C 30 200, 70 220, 80 260 C 88 290, 40 310, 0 310 Z" fill="#D6ECD6" data-id="element-5894" data-mp="2o0863cyqlvf" />
          {/* river */}
          <path d="M118 -10 C 100 60, 150 110, 120 170 C 95 220, 130 260, 104 320" stroke="#D5E5EC" strokeWidth="34" fill="none" data-id="element-5895" data-mp="hatggoo2ere4" />
          {/* west bank streets */}
          {[50, 110, 170, 230, 290].map(y => <path key={y} d={`M0 ${y} C 30 ${y - 6}, 60 ${y + 6}, 96 ${y}`} stroke="#FFFFFF" strokeWidth="2" fill="none" data-id="element-5896" data-mp="lehmv40yngxs" />)}

          {/* sectors */}
          {sectorGrid.map((row, r) => row.map((status, c) => {
          const selected = r === selectedSector.row && c === selectedSector.col;
          return <path key={`${r}-${c}`} d={cellPath(r, c)} fill={(r + c) % 2 === 0 ? FILL[status] : FILL_SOFT[status]} fillOpacity={selected ? 1 : 0.85} stroke="#FFFFFF" strokeWidth={selected ? 3 : 2} data-id="element-5897" data-mp="2pluizbjsm304" data-mpi={c} />;
        }))}
          {/* selected sector outline */}
          <path d={cellPath(selectedSector.row, selectedSector.col)} fill="none" stroke="#C93F3F" strokeWidth="1.5" strokeDasharray="4 3" data-id="element-5898" data-mp="25y3l3slcq15w" />

          {/* main roads across sectors */}
          <path d="M140 150 C 260 130, 380 170, 560 140" stroke="#FFFFFF" strokeWidth="4" fill="none" opacity="0.9" data-id="element-5899" data-mp="3m8z8niwkvore" />
          <path d="M330 0 C 320 90, 350 200, 330 310" stroke="#FFFFFF" strokeWidth="4" fill="none" opacity="0.9" data-id="element-5900" data-mp="81mp7w3ol1p" />
          <path d="M150 60 C 260 80, 420 40, 560 70" stroke="#FFFFFF" strokeWidth="1.5" fill="none" opacity="0.8" data-id="element-5901" data-mp="3rgxjdbatf5ic" />
          <path d="M150 240 C 260 220, 420 260, 560 230" stroke="#FFFFFF" strokeWidth="1.5" fill="none" opacity="0.8" data-id="element-5902" data-mp="3mlrr7uqtkult" />
          <path d="M440 0 C 450 100, 430 200, 450 310" stroke="#FFFFFF" strokeWidth="1.5" fill="none" opacity="0.8" data-id="element-5903" data-mp="3dnz78da5kys3" />
          <path d="M230 0 C 240 100, 215 200, 235 310" stroke="#FFFFFF" strokeWidth="1.5" fill="none" opacity="0.8" data-id="element-5904" data-mp="38o3bf1bm0dnm" />

          {/* selected pin */}
          <circle cx={cx} cy={cy} r="6" fill="#FFFFFF" stroke="#C93F3F" strokeWidth="2" data-id="element-5905" data-mp="33r38gza23r0q" />
          <circle cx={cx} cy={cy} r="2.2" fill="#C93F3F" data-id="element-5906" data-mp="2zprfqkvvypob" />
        </svg>

        {/* zoom */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex flex-col rounded-md border border-[#E3E4EA] bg-white shadow-sm" aria-hidden="true" data-id="element-5907" data-mp="11x3w0al773mg">
          <span className="w-7 h-7 flex items-center justify-center border-b border-[#EEEFF3]" data-id="element-5908" data-mp="2resfikqef4lr">
            <PlusIcon className="w-3.5 h-3.5 text-[#3A4050]" data-id="element-5909" data-mp="2w60q3k3gsw0i" />
          </span>
          <span className="w-7 h-7 flex items-center justify-center" data-id="element-5910" data-mp="2iem6pc5nassx">
            <MinusIcon className="w-3.5 h-3.5 text-[#3A4050]" data-id="element-5911" data-mp="2ic9fs9hwt83q" />
          </span>
        </div>

        {/* tooltip */}
        <div className="absolute rounded-lg bg-[#1E2230] px-3 py-2 shadow-lg pointer-events-none" style={{
        left: `${cx / 560 * 100 + 3}%`,
        top: `${cy / 310 * 100 - 30}%`
      }} data-id="element-5912" data-mp="3a5ikcezj51h9">
          <p className="font-sans text-[11px] font-medium text-white" data-id="element-5913" data-mp="3153wp8z3faal">{isFR ? sectorDetail.name.fr : sectorDetail.name.en}</p>
          <p className="font-sans text-[11px] font-semibold text-[#FF8A8A]" data-id="element-5914" data-mp="3635elqks93kh">{isFR ? sectorDetail.sla.fr : sectorDetail.sla.en}</p>
          <p className="font-sans text-[10.5px] text-white/85" data-id="element-5915" data-mp="1po3wlr6i7r60">{isFR ? sectorDetail.requests.fr : sectorDetail.requests.en}</p>
        </div>

        {/* legend */}
        <ul className="absolute left-3 bottom-2.5 flex flex-wrap gap-x-4 gap-y-1 rounded-md bg-white/85 px-2 py-1" data-id="element-5916" data-mp="1m6zoniuotte5">
          {([['#E85B5B', t('Sous la cible', 'Below target')], ['#F0B24A', t('À surveiller', 'Needs attention')], ['#3DBE63', t('En bonne voie', 'On track')]] as const).map(([color, label]) => <li key={label} className="flex items-center gap-1.5 font-sans text-[10.5px] text-[#4A5160]" data-id="element-5917" data-mp="1r51844w0zuq5">
              <span className="w-2.5 h-2.5 rounded-[3px]" style={{
            backgroundColor: color
          }} aria-hidden="true" data-id="element-5918" data-mp="yi6p7ceux3es" />
              {label}
            </li>)}
        </ul>
      </div>
    </div>;
}